import cn from 'classnames';
import { Form } from 'formik';
import React, {
  ChangeEvent,
  FC,
  ReactElement,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  getDatePickerDateFormat,
  getDatePickerLocale,
  getErrorText,
  getLocaleDateFormat,
  hasError,
  selectChangeCheck,
} from '@utils/utils';

import {
  Button,
  Datepicker,
  Ellipsis,
  ErrorText,
  IconTimes,
  ILibHeading,
  ILibOption,
  Input,
  LibFixedType,
  SelectWrapper,
  Table,
} from '@infologistics/frontend-libraries';

import { Nullable } from '@app/types';
import { ISelectedCertificatePowerFormProps as IProps } from './types';

import styles from '../CertificatePower.module.css';
import { useAppSelector } from '@app/hooks/redux';
import { DateTime } from 'luxon';
import { IMandate } from '@app/store/modules/user/types';
import { Warning } from '@app/const/translations';

const getFullName = (guid: string, name: string): string => `${guid}.${name}`;

const CertificatePowerForm: FC<IProps> = (props) => {
  const {
    errors,
    isEdit,
    isSigning,
    isSigningWithout,
    inProgress,
    onClose,
    onPoAAdd,
    onPoARemove,
    onPoASelect,
    setFieldValue,
    touched,
    values,
    boxSelectDisabled,
  } = props;

  const { t } = useTranslation();

  const { boxes } = useAppSelector((state) => state.boxes);
  const appLocale = useAppSelector((state) => state.user.profile.locale);

  const [activeRowId, setActiveRowId] = useState<string>('');
  const [boxOptions, setBoxOptions] = useState<ILibOption[]>([]);

  useEffect(() => {
    const initBoxOptions: ILibOption[] = boxes.map((val) => ({
      label: val.email,
      value: val.oguid,
      info: val.companyName,
    }));

    setBoxOptions(initBoxOptions);
  }, []);

  const tableHeadings: ILibHeading[] = [
    {
      class: styles.index,
      content: '№',
    },
    {
      class: styles.guid,
      content: t('profile:PoA.guid'),
    },
    {
      class: styles.date,
      content: t('profile:PoA.date'),
    },
    {
      class: styles.date,
      content: t('profile:PoA.validFrom'),
    },
    {
      class: styles.date,
      content: t('profile:PoA.validTo'),
    },
    ...(boxSelectDisabled
      ? []
      : [
          {
            class: styles.type,
            content: t('profile:PoA.box'),
          },
        ]),
    {
      class: styles.org,
      content: t('profile:PoA.organizationName'),
    },
    {
      class: styles.inn,
      content: t('profile:PoA.organizationInn'),
    },
    {
      class: styles.desc,
      content: t('profile:PoA.desc'),
    },
  ];

  const editTableHeadings: ILibHeading[] = [
    ...tableHeadings,
    {
      content: ' ',
    },
  ];

  const handleSelectChange =
    (objIdx: string, name: string) =>
    ({ value }: ILibOption) => {
      setFieldValue(getFullName(objIdx, name), value);
    };

  const handleTextInputChange =
    (objIdx: string) => (evt: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = evt.target;

      const newValue = name === 'inn' ? value + '' : value;

      setFieldValue(getFullName(objIdx, name), newValue);
    };

  const handleDateInputChange =
    (objIdx: string, name: string) => (date: Nullable<Date>) => {
      setFieldValue(
        getFullName(objIdx, name),
        date
          ? DateTime.fromJSDate(date).toISO({ suppressMilliseconds: true })
          : null
      );
    };

  const handleRowFocusMouseOver = (id: string) => () => {
    setActiveRowId(id);
  };

  const renderRowRemoveButton = (objIdx: string): ReactElement => (
    <Button
      externalClass={styles.close_button}
      theme='text'
      onClick={onPoARemove(objIdx)}
      isDisabled={inProgress}
    >
      <IconTimes color='gray-500' />
    </Button>
  );

  const renderTableRow = (
    value: [objIdx: string, val: IMandate],
    index: number
  ): ReactElement => {
    const [objIdx, val] = value;
    const {
      oguid,
      guid,
      description,
      issuedDate,
      boxOguid,
      organizationName,
      organizationInn,
      validFrom,
      validTo,
    } = val;

    const boxTitle = boxOptions.find((val) => val.value === boxOguid)?.label;

    return (
      <tr
        className={`pointer ${isSigning ? styles.pointer_disabled : ''}`}
        key={objIdx}
        onClick={oguid ? onPoASelect(oguid) : undefined}
      >
        <td className={cn(styles.index, 'text-middle')}>{`${index + 1}.`}</td>
        <td className={cn(styles.guid, 'text-middle')}>
          <Ellipsis title={guid}>{guid}</Ellipsis>
        </td>
        <td className={cn(styles.date, 'text-middle')}>
          <Ellipsis>
            {getLocaleDateFormat(issuedDate, appLocale, DateTime.DATE_SHORT)}
          </Ellipsis>
        </td>
        <td className={cn(styles.date, 'text-middle')}>
          <Ellipsis>
            {getLocaleDateFormat(validFrom, appLocale, DateTime.DATE_SHORT)}
          </Ellipsis>
        </td>
        <td className={cn(styles.date, 'text-middle')}>
          <Ellipsis>
            {getLocaleDateFormat(validTo, appLocale, DateTime.DATE_SHORT)}
          </Ellipsis>
        </td>
        {!boxSelectDisabled && (
          <td className={cn(styles.type, 'text-middle')}>
            <Ellipsis title={boxTitle}>{boxTitle}</Ellipsis>
          </td>
        )}
        <td className={cn(styles.org, 'text-middle')}>
          <Ellipsis title={organizationName}>{organizationName}</Ellipsis>
        </td>
        <td className={cn(styles.inn, 'text-middle')}>
          <Ellipsis>{organizationInn}</Ellipsis>
        </td>
        <td className={cn(styles.desc, 'text-middle')}>
          <Ellipsis title={description ?? undefined}>{description}</Ellipsis>
        </td>
      </tr>
    );
  };

  const renderEditableRow = (
    value: [objIdx: string, val: IMandate],
    index: number
  ): ReactElement => {
    const [objIdx, val] = value;
    const {
      guid,
      description,
      issuedDate,
      boxOguid,
      organizationName,
      organizationInn,
      validFrom,
      validTo,
    } = val;

    const isActive = activeRowId === objIdx;

    const className = isActive ? styles.active_item : styles.inactive_item;

    const errorRequest =
      hasError(objIdx, errors, touched, true) &&
      typeof errors[objIdx] === 'string';

    return (
      <React.Fragment key={objIdx}>
        <tr
          className={className}
          onFocus={handleRowFocusMouseOver(objIdx)}
          onMouseOver={handleRowFocusMouseOver(objIdx)}
          key={objIdx}
        >
          <td
            className={cn('text-middle text-center', styles.index, {
              ['bg-danger']: errorRequest,
            })}
          >
            {`${index + 1}.`}
          </td>
          <td className={cn('text-middle', styles.guid)}>
            <Input
              name='guid'
              onChange={handleTextInputChange(objIdx)}
              value={guid ?? null}
              hasError={hasError(getFullName(objIdx, 'guid'), errors, touched)}
              disabled={inProgress}
              title={guid}
              errorText={t(
                getErrorText(
                  getFullName(objIdx, 'guid'),
                  errors,
                  Warning.required
                )
              )}
            />
          </td>
          <td className={cn('text-middle', styles.date)}>
            <Datepicker
              autoComplete='off'
              dateFormat={getDatePickerDateFormat(appLocale)}
              locale={getDatePickerLocale(appLocale)}
              hasError={hasError(
                getFullName(objIdx, 'issuedDate'),
                errors,
                touched
              )}
              name='issuedDate'
              onChange={handleDateInputChange(objIdx, 'issuedDate')}
              selected={issuedDate ? new Date(issuedDate) : null}
              disabled={inProgress}
            />
          </td>
          <td className={cn('text-middle', styles.date)}>
            <Datepicker
              autoComplete='off'
              dateFormat={getDatePickerDateFormat(appLocale)}
              locale={getDatePickerLocale(appLocale)}
              hasError={hasError(
                getFullName(objIdx, 'validFrom'),
                errors,
                touched
              )}
              name='validFrom'
              onChange={handleDateInputChange(objIdx, 'validFrom')}
              selected={validFrom ? new Date(validFrom) : null}
              disabled={inProgress}
              maxDate={
                validTo
                  ? DateTime.fromISO(validTo).minus({ day: 1 }).toJSDate()
                  : null
              }
            />
          </td>
          <td className={cn('text-middle', styles.date)}>
            <Datepicker
              autoComplete='off'
              dateFormat={getDatePickerDateFormat(appLocale)}
              locale={getDatePickerLocale(appLocale)}
              hasError={hasError(
                getFullName(objIdx, 'validTo'),
                errors,
                touched
              )}
              name='validTo'
              onChange={handleDateInputChange(objIdx, 'validTo')}
              selected={validTo ? new Date(validTo) : null}
              disabled={inProgress}
              minDate={
                validFrom
                  ? DateTime.fromISO(validFrom).plus({ day: 1 }).toJSDate()
                  : null
              }
            />
          </td>
          {!boxSelectDisabled && (
            <td className={cn(styles.select, styles.type, 'text-middle')}>
              <SelectWrapper
                name='boxOguid'
                onChange={selectChangeCheck(
                  handleSelectChange(objIdx, 'boxOguid')
                )}
                options={boxOptions}
                rawValue={boxOguid}
                placeholder=''
                isDisabled={boxSelectDisabled || inProgress}
                hasError={hasError(
                  getFullName(objIdx, 'boxOguid'),
                  errors,
                  touched
                )}
              />
            </td>
          )}
          <td className={cn('text-middle', styles.org)}>
            <Input
              name='organizationName'
              onChange={handleTextInputChange(objIdx)}
              value={organizationName ?? null}
              title={organizationName}
              disabled={inProgress}
              hasError={hasError(
                getFullName(objIdx, 'organizationName'),
                errors,
                touched
              )}
              errorText={t(
                getErrorText(
                  getFullName(objIdx, 'organizationName'),
                  errors,
                  Warning.required
                )
              )}
            />
          </td>
          <td className={cn('text-middle', styles.inn)}>
            <Input
              name='organizationInn'
              onChange={handleTextInputChange(objIdx)}
              value={organizationInn}
              disabled={inProgress}
              className={styles.inn_input}
              hasError={hasError(
                getFullName(objIdx, 'organizationInn'),
                errors,
                touched
              )}
              errorText={t(
                getErrorText(
                  getFullName(objIdx, 'organizationInn'),
                  errors,
                  Warning.required
                )
              )}
            />
          </td>
          <td className={cn('text-middle', styles.desc)}>
            <Input
              hasError={hasError(
                getFullName(objIdx, 'description'),
                errors,
                touched
              )}
              name='description'
              title={description}
              onChange={handleTextInputChange(objIdx)}
              value={description ?? null}
              disabled={inProgress}
              errorText={t(
                getErrorText(
                  getFullName(objIdx, 'description'),
                  errors,
                  Warning.required
                )
              )}
            />
          </td>
          <td className='text-middle'>{renderRowRemoveButton(objIdx)}</td>
        </tr>
        {errorRequest && (
          <tr>
            <td colSpan={9} style={{ height: '30px' }}>
              <ErrorText
                errorText={getErrorText(objIdx, errors, Warning.required)}
                isAbsolute={false}
              />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <Form>
      <div
        className={cn(
          'd-flex align-items-start flex-column p-3 scrollbar-content',
          styles.body
        )}
      >
        <Table
          classes={cn('mb-auto', {
            [styles.editable]: isEdit,
          })}
          headings={isEdit ? editTableHeadings : tableHeadings}
          fixedModalHeight={!isEdit ? '164px' : undefined}
          fixedType={!isEdit ? LibFixedType.MODAL : undefined}
          isUpdateTableY
        >
          {Object.entries(values).map(
            isEdit ? renderEditableRow : renderTableRow
          )}
        </Table>
        {isEdit && (
          <Button
            classes='mt-4'
            theme='link'
            linkStyle='none'
            onClick={onPoAAdd}
            isDisabled={inProgress}
          >
            {t('profile:PoA.add')}
          </Button>
        )}
      </div>
      <div className='align-items-center d-flex modal-footer justify-content-end'>
        {(!isEdit || boxSelectDisabled) && (
          <Button classes='mr-4' onClick={onClose} isDisabled={inProgress}>
            {t('common:back')}
          </Button>
        )}
        <Button
          type='submit'
          theme='primary'
          loading={isSigningWithout || inProgress}
          isDisabled={inProgress}
        >
          {isEdit ? t('common:save') : t('profile:PoA.signWithout')}
        </Button>
        {isEdit && !boxSelectDisabled && (
          <Button
            classes='ml-4'
            type='button'
            onClick={onClose}
            isDisabled={inProgress}
          >
            {t('common:close')}
          </Button>
        )}
      </div>
      {/* <div className={cn(styles.spiner, 'absolute')}>
            <Spinner />
          </div> */}
    </Form>
  );
};

export default CertificatePowerForm;
